import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FaUsers, FaPlus, FaSearch, FaTrash } from 'react-icons/fa';
import Navbar from '../student/Navbar';
import Loader from '../components/Loader';
import ConfirmModal from '../components/ConfirmModal';
import SuccessModal from '../components/SuccessModal';
import { useAuth } from '../context/AuthContext';
import SuperAdminSidebar from './SuperAdminSidebar';

const Accounts = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [universities, setUniversities] = useState([]);
  const [clinics, setClinics] = useState([]);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successTitle, setSuccessTitle] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordId, setResetPasswordId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showResetAllPasswordsModal, setShowResetAllPasswordsModal] = useState(false);
  const [defaultPassword, setDefaultPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showDefaultPassword, setShowDefaultPassword] = useState(false);
  // Password display removed as requested
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'student',
    studentId: '',
    universityId: '',
    clinicId: '',
    affiliationType: 'university', // 'university' or 'dentist' for assistants
    dentistId: '',
    accountType: 'regular', // 'regular' or 'google' or 'microsoft'
  });

  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const fetchAccounts = async () => {
      if (!user || !token) {
        setError('Please log in to view accounts.');
        setLoading(false);
        return;
      }

      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        console.log('🔍 Fetching accounts from:', `${process.env.REACT_APP_API_URL}/api/accounts`);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/accounts`, config);
        console.log('📊 Initial accounts loaded:', response.data?.length || 0);
        console.log('📋 IT accounts in initial load:', response.data?.filter(acc => acc.role === 'it') || []);
        setAccounts(response.data || []);
        setFilteredAccounts(response.data || []);
      } catch (err) {
        const errorMessage = err.response?.status === 401
          ? 'Unauthorized. Please log in again.'
          : err.response?.data?.message || 'Failed to load accounts';
        setError(errorMessage);
        if (err.response?.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchAccounts();
  }, [user, token, navigate]);

  useEffect(() => {
    const fetchUniversities = async () => {
      if (!user || !token) return;

      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/universities`, config);
        setUniversities(response.data || []);
      } catch (err) {
      }
    };

    fetchUniversities();
  }, [user, token]);

  useEffect(() => {
    const fetchClinics = async () => {
      if (!user || !token) return;

      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/clinics`, config);
        setClinics(response.data || []);
      } catch (err) {
      }
    };

    fetchClinics();
  }, [user, token]);



  useEffect(() => {
    const filtered = accounts.filter(account => {
      const matchesSearch = account.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           account.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = roleFilter ? account.role === roleFilter : true;
      return matchesSearch && matchesRole;
    });
    setFilteredAccounts(filtered);
  }, [searchTerm, roleFilter, accounts]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('Please enter a valid email address (e.g., user@domain.com)');
        return;
      }

      // Validate form based on role
      if (formData.role === 'student' && !formData.studentId) {
        setError('Student ID is required for student accounts');
        return;
      }

      if (['student', 'supervisor', 'admin', 'it'].includes(formData.role) && !formData.universityId) {
        setError('University ID is required for this role');
        return;
      }

      if (formData.role === 'assistant') {
        if (!formData.affiliationType) {
          setError('Affiliation type is required for assistant accounts');
          return;
        }
        if (formData.affiliationType === 'university' && !formData.universityId) {
          setError('University ID is required for university-affiliated assistants');
          return;
        }
        if (formData.affiliationType === 'dentist' && !formData.dentistId) {
          setError('Dentist ID is required for dentist-affiliated assistants');
          return;
        }
      }

      if (formData.role === 'dentist' && !formData.clinicId) {
        setError('Clinic is required for dentist accounts');
        return;
      }

      // Validate password for regular accounts
      if (formData.accountType === 'regular' && !formData.password) {
        setError('Password is required for regular accounts');
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      // Clean formData based on role and account type
      const cleanedData = { ...formData };

      // Remove studentId if not student
      if (formData.role !== 'student') {
        delete cleanedData.studentId;
      }

      // Remove universityId if not needed
      if (!(['student', 'supervisor', 'admin', 'it'].includes(formData.role) || (formData.role === 'assistant' && formData.affiliationType === 'university'))) {
        delete cleanedData.universityId;
      }
      // Remove dentistId if not assistant with dentist affiliation or if empty
      if (!(formData.role === 'assistant' && formData.affiliationType === 'dentist' && formData.dentistId)) {
        delete cleanedData.dentistId;
      }

      // Remove clinicId if not dentist
      if (formData.role !== 'dentist') {
        delete cleanedData.clinicId;
      }

      // Remove affiliationType if not assistant
      if (formData.role !== 'assistant') {
        delete cleanedData.affiliationType;
      }

      // For Google/Microsoft accounts, remove password and add flags
      if (formData.accountType === 'google') {
        delete cleanedData.password;
        cleanedData.isGoogleAccount = true;
      } else if (formData.accountType === 'microsoft') {
        delete cleanedData.password;
        cleanedData.isMicrosoftAccount = true;
      }

      // Remove accountType from the data sent to backend
      delete cleanedData.accountType;

      // Create account with sanitized data

      // Use different endpoint for Google/Microsoft accounts
      const endpoint = formData.accountType === 'google'
        ? `${process.env.REACT_APP_API_URL}/api/accounts/google`
        : formData.accountType === 'microsoft'
          ? `${process.env.REACT_APP_API_URL}/api/accounts/microsoft`
          : `${process.env.REACT_APP_API_URL}/api/accounts`;

      console.log('🔍 Frontend sending data:', {
        endpoint,
        data: cleanedData,
        accountType: formData.accountType
      });

      console.log('📤 Sending request to:', endpoint);
      const response = await axios.post(endpoint, cleanedData, config);
      console.log('✅ Account creation response:', response.data);
      
      setShowModal(false);
      setFormData({
        email: '',
        password: '',
        name: '',
        role: 'student',
        studentId: '',
        universityId: '',
        clinicId: '',
        affiliationType: 'university',
        dentistId: '',
        accountType: 'regular',
      });
      setError('');
      // Refresh accounts
      console.log('🔄 Refreshing accounts list...');
      const accountsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/accounts`, config);
      console.log('📊 Refreshed accounts:', accountsResponse.data?.length || 0);
      console.log('📋 IT accounts in response:', accountsResponse.data?.filter(acc => acc.role === 'it') || []);
      setAccounts(accountsResponse.data || []);
      setFilteredAccounts(accountsResponse.data || []);
      console.log('✅ Accounts list refreshed');

      // Show success message
      setSuccessTitle('Account Added');
      setSuccessMessage(`${formData.accountType === 'google' ? 'Google account' : formData.accountType === 'microsoft' ? 'Microsoft account' : 'Account'} has been added successfully!`);
      setShowSuccessModal(true);
    } catch (err) {
      console.error('❌ Account creation error:', err);
      console.error('❌ Error response:', err.response?.data);
      console.error('❌ Error status:', err.response?.status);
      setError(err.response?.data?.message || 'Failed to create account');
    }
  };

  const handleAccountClick = (account) => {
    setSelectedAccount(account);
    setShowDetailsModal(true);
  };

  const handleDeleteClick = (e, accountId) => {
    e.stopPropagation();
    setSelectedAccountId(accountId);
    setShowConfirmModal(true);
  };

  const handleResetPasswordClick = (e, accountId) => {
    e.stopPropagation();
    setResetPasswordId(accountId);
    setNewPassword('');
    setShowResetPasswordModal(true);
  };

  const handleResetPassword = async () => {
    try {
      if (!newPassword || newPassword.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${process.env.REACT_APP_API_URL}/api/accounts/${resetPasswordId}/reset-password`,
        { newPassword },
        config
      );

      setShowResetPasswordModal(false);
      setNewPassword('');

      // Show success message
      setSuccessTitle('Password Reset');
      setSuccessMessage('Password has been reset successfully!');
      setShowSuccessModal(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password');
    }
  };

  const handleResetAllPasswords = async () => {
    try {
      if (!defaultPassword || defaultPassword.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/reset-all-passwords`,
        { defaultPassword },
        config
      );

      setShowResetAllPasswordsModal(false);
      setDefaultPassword('');

      // Show success message
      setSuccessTitle('All Passwords Reset');
      setSuccessMessage(`All passwords have been reset successfully! ${JSON.stringify(response.data.results)}`);
      setShowSuccessModal(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset all passwords');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/accounts/${selectedAccountId}`, config);
      setAccounts(accounts.filter((a) => a.id !== selectedAccountId));
      setShowConfirmModal(false);
      setSelectedAccountId(null);

      // Show success message
      setSuccessTitle('Account Deleted');
      setSuccessMessage('Account has been deleted successfully!');
      setShowSuccessModal(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete account');
      setShowConfirmModal(false);
    }
  };

  const container = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <SuperAdminSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

        <main className="flex-1 overflow-y-auto p-6 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-7xl mx-auto">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm"
              >
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mr-3" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <p className="text-red-700 font-medium">{error}</p>
                </div>
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-[#0077B6] mb-1">Accounts</h1>
                  <p className="text-[#333333]">Manage user accounts and permissions</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setDefaultPassword('');
                      setShowResetAllPasswordsModal(true);
                    }}
                    className="w-full md:w-auto bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-6 py-3 rounded-full font-medium transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    Reset All Passwords
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowModal(true)}
                    className="w-full md:w-auto bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white px-6 py-3 rounded-full font-medium transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center"
                  >
                    <FaPlus className="h-5 w-5 mr-2" />
                    Add Account
                  </motion.button>
                </div>
              </div>

              <div className="mb-6 flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <FaSearch className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by email or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                  />
                </div>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                >
                  <option value="">All Roles</option>
                  <option value="student">Student</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="admin">Admin</option>
                  <option value="assistant">Assistant</option>
                  <option value="dentist">Dentist</option>
                  <option value="it">IT</option>
                </select>
              </div>

              <motion.div
                variants={container}
                initial="hidden"
                animate="show"
                key={accounts.length} // Force re-render when accounts change
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 overflow-hidden"
              >
                <div className="p-6">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">University ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Affiliation</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredAccounts.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="px-6 py-8 text-center">
                              <div className="flex flex-col items-center justify-center">
                                <FaUsers className="h-12 w-12 text-gray-400 mb-4" />
                                <h3 className="text-lg font-medium text-gray-900">No accounts found</h3>
                                <p className="mt-1 text-gray-500">Add an account or adjust filters.</p>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          filteredAccounts.map((account, index) => (
                            <motion.tr
                              key={account.id || account.email + index} // Use account.id for unique key
                              variants={item}
                              className="hover:bg-gray-50 cursor-pointer"
                              onClick={() => handleAccountClick(account)}
                            >
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{account.email}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{account.name}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{account.role}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div className="flex items-center">
                                  {account.googleId || account.isGoogleAccount ? (
                                    <div className="flex items-center">
                                      <svg className="h-4 w-4 text-red-500 mr-1" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                      </svg>
                                      <span className="text-xs text-gray-600">Google</span>
                                    </div>
                                  ) : account.microsoftId || account.isMicrosoftAccount ? (
                                    <div className="flex items-center">
                                      <svg className="h-4 w-4 mr-1" viewBox="0 0 24 24" aria-hidden="true">
                                        <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                                        <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
                                        <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
                                        <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
                                      </svg>
                                      <span className="text-xs text-gray-600">Microsoft</span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center">
                                      <svg className="h-4 w-4 text-gray-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                      </svg>
                                      <span className="text-xs text-gray-600">Regular</span>
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{account.universityId || 'N/A'}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {account.role === 'assistant' ? (
                                  <div className="flex items-center">
                                    {account.affiliation?.type === 'dentist' ? (
                                      <div className="flex items-center">
                                        <svg className="h-4 w-4 text-blue-500 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        <span className="text-xs text-gray-600">Dentist</span>
                                      </div>
                                    ) : (
                                      <div className="flex items-center">
                                        <svg className="h-4 w-4 text-green-500 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                                        </svg>
                                        <span className="text-xs text-gray-600">University</span>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  'N/A'
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{account.studentId || 'N/A'}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div className="flex space-x-3">
                                  {!(account.googleId || account.isGoogleAccount || account.microsoftId || account.isMicrosoftAccount) && (
                                    <button
                                      onClick={(e) => handleResetPasswordClick(e, account.id)}
                                      className="text-[#0077B6] hover:text-[#20B2AA]"
                                      title="Reset Password"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                      </svg>
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => handleDeleteClick(e, account.id)}
                                    className="text-red-600 hover:text-red-800"
                                    title="Delete"
                                  >
                                    <FaTrash className="h-5 w-5" />
                                  </button>
                                </div>
                              </td>
                            </motion.tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </main>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-[#0077B6]">Add Account</h2>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Account Type*</label>
                    <select
                      name="accountType"
                      value={formData.accountType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                      required
                    >
                      <option value="regular">Regular Account</option>
                      <option value="google">Google Account</option>
                      <option value="microsoft">Microsoft Account</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email*</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  {formData.accountType === 'regular' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Password*</label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA] pr-10"
                          required
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                              <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                              <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                  {formData.accountType === 'google' && (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <div className="flex items-center">
                        <svg className="h-5 w-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm text-blue-700">
                          Google accounts will authenticate using Google OAuth. No password required.
                        </span>
                      </div>
                    </div>
                  )}
                  {formData.accountType === 'microsoft' && (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <div className="flex items-center">
                        <svg className="h-5 w-5 text-blue-500 mr-2" viewBox="0 0 24 24" aria-hidden="true">
                          <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                          <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
                          <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
                          <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
                        </svg>
                        <span className="text-sm text-blue-700">
                          Microsoft accounts will authenticate using Microsoft OAuth. No password required.
                        </span>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name*</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Role*</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                      required
                    >
                      <option value="student">Student</option>
                      <option value="supervisor">Supervisor</option>
                      <option value="admin">Admin</option>
                      <option value="assistant">Assistant</option>
                      <option value="dentist">Dentist</option>
                      <option value="it">IT</option>
                    </select>
                  </div>
                  {formData.role === 'student' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Student ID*</label>
                      <input
                        type="text"
                        name="studentId"
                        value={formData.studentId}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                  )}
                  {formData.role === 'assistant' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Affiliation Type*</label>
                      <select
                        name="affiliationType"
                        value={formData.affiliationType}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="university">University</option>
                        <option value="dentist">Dentist</option>
                      </select>
                    </div>
                  )}
                  {formData.role === 'assistant' && formData.affiliationType === 'university' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">University*</label>
                      <select
                        name="universityId"
                        value={formData.universityId}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">Select University</option>
                        {universities.map((university) => (
                          <option key={university.universityId} value={university.universityId}>
                            {university.name.en} ({university.universityId})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {formData.role === 'assistant' && formData.affiliationType === 'dentist' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Dentist ID</label>
                      <input
                        type="text"
                        name="dentistId"
                        value={formData.dentistId}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter dentist ID (optional)"
                      />
                    </div>
                  )}
                  {['student', 'supervisor', 'admin', 'it'].includes(formData.role) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">University*</label>
                      <select
                        name="universityId"
                        value={formData.universityId}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">Select University</option>
                        {universities.map((university) => (
                          <option key={university.universityId} value={university.universityId}>
                            {university.name.en} ({university.universityId})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Clinic dropdown for dentists */}
                  {formData.role === 'dentist' && (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Clinic *
                      </label>
                      <select
                        name="clinicId"
                        value={formData.clinicId || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        required
                      >
                        <option value="">Select a clinic</option>
                        {clinics.map((clinic) => (
                          <option key={clinic.id} value={clinic.clinicId}>
                            {clinic.name.en} - {clinic.specialization.en}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}


                </div>
                <div className="flex justify-end space-x-4 pt-4">
                  <motion.button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    type="submit"
                    className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-lg hover:from-blue-600 hover:to-blue-800 font-medium transition-colors shadow-md hover:shadow-lg"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Add Account
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}

      {showDetailsModal && selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl"
          >
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center">
                  <div className="bg-gradient-to-r from-blue-500 to-blue-700 text-white p-3 rounded-full mr-4">
                    {selectedAccount.role === 'student' && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path d="M12 14l9-5-9-5-9 5 9 5z" />
                        <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
                      </svg>
                    )}
                    {selectedAccount.role === 'supervisor' && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    )}
                    {selectedAccount.role === 'admin' && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                    {selectedAccount.role === 'assistant' && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    )}
                    {selectedAccount.role === 'dentist' && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    )}
                    {selectedAccount.role === 'it' && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                      </svg>
                    )}

                    {selectedAccount.role === 'superadmin' && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-blue-900">{selectedAccount.name}</h2>
                    <p className="text-gray-500 capitalize">{selectedAccount.role}</p>
                  </div>
                </div>
                <button onClick={() => setShowDetailsModal(false)} className="text-gray-400 hover:text-gray-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Account Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-gray-900 font-medium">{selectedAccount.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">ID</label>
                    <p className="mt-1 text-gray-900 font-medium">{selectedAccount.id}</p>
                  </div>
                  {/* Password field removed as requested */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Role</label>
                    <p className="mt-1 text-gray-900 font-medium capitalize">{selectedAccount.role}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Created At</label>
                    <p className="mt-1 text-gray-900 font-medium">
                      {selectedAccount.createdAt ? new Date(selectedAccount.createdAt).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Updated</label>
                    <p className="mt-1 text-gray-900 font-medium">
                      {selectedAccount.updatedAt ? new Date(selectedAccount.updatedAt).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Role-Specific Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {selectedAccount.universityId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">University ID</label>
                      <p className="mt-1 text-gray-900 font-medium">{selectedAccount.universityId}</p>
                    </div>
                  )}
                  {selectedAccount.studentId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Student ID</label>
                      <p className="mt-1 text-gray-900 font-medium">{selectedAccount.studentId}</p>
                    </div>
                  )}

                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <motion.button
                  onClick={() => {
                    setResetPasswordId(selectedAccount.id);
                    setNewPassword('');
                    setShowResetPassword(false);
                    setShowDetailsModal(false);
                    setShowResetPasswordModal(true);
                  }}
                  className="px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 font-medium transition-colors flex items-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  Reset Password
                </motion.button>
                <motion.button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Close
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this account? This action cannot be undone."
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={successTitle}
        message={successMessage}
      />

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-blue-900">Reset Password</h2>
                <button onClick={() => setShowResetPasswordModal(false)} className="text-gray-400 hover:text-gray-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">New Password*</label>
                  <div className="relative">
                    <input
                      type={showResetPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10"
                      placeholder="Enter new password"
                      required
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                      onClick={() => setShowResetPassword(!showResetPassword)}
                    >
                      {showResetPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                          <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">Password must be at least 6 characters.</p>
                </div>
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <motion.button
                  type="button"
                  onClick={() => setShowResetPasswordModal(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  type="button"
                  onClick={handleResetPassword}
                  className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-lg hover:from-blue-600 hover:to-blue-800 font-medium transition-colors shadow-md hover:shadow-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Reset Password
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Reset All Passwords Modal */}
      {showResetAllPasswordsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-yellow-600">Reset All Passwords</h2>
                <button onClick={() => setShowResetAllPasswordsModal(false)} className="text-gray-400 hover:text-gray-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-gray-700 mb-4">
                    This will reset the passwords for <strong>ALL</strong> accounts in the system.
                    Are you sure you want to continue?
                  </p>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Default Password*</label>
                  <div className="relative">
                    <input
                      type={showDefaultPassword ? "text" : "password"}
                      value={defaultPassword}
                      onChange={(e) => setDefaultPassword(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10"
                      placeholder="Enter default password for all accounts"
                      required
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                      onClick={() => setShowDefaultPassword(!showDefaultPassword)}
                    >
                      {showDefaultPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                          <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">Password must be at least 6 characters.</p>
                </div>
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <motion.button
                  type="button"
                  onClick={() => setShowResetAllPasswordsModal(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  type="button"
                  onClick={handleResetAllPasswords}
                  className="px-6 py-2 bg-gradient-to-r from-yellow-500 to-yellow-700 text-white rounded-lg hover:from-yellow-600 hover:to-yellow-800 font-medium transition-colors shadow-md hover:shadow-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Reset All Passwords
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Accounts;